import { HiOutlineUser, HiOutlineLifebuoy, HiPlus, HiSparkles } from 'react-icons/hi2';
import { FaSms, FaWhatsapp } from 'react-icons/fa';
import { ChatAvatar, ConversationList, ConversationRow } from '@reloam/ui';
import { formatConversationTime, type ChannelKind, type SwitcherRow } from './conversationOrder';

const CHANNEL_LABEL: Record<ChannelKind, string> = {
  assistant: 'AI assistant',
  tenant: 'Direct message',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  service_desk: 'Service desk',
};

export function ChannelAvatar({ kind, size = 'md', onPrimary = false }: { kind: ChannelKind; size?: 'sm' | 'md'; onPrimary?: boolean }) {
  const icon = {
    assistant: HiSparkles,
    tenant: HiOutlineUser,
    sms: FaSms,
    whatsapp: FaWhatsapp,
    service_desk: HiOutlineLifebuoy,
  }[kind];
  return <ChatAvatar icon={icon} size={size} onPrimary={onPrimary} tone={kind === 'assistant' ? 'accent' : 'neutral'} />;
}

interface ConversationSwitcherProps {
  rows: SwitcherRow[];
  onSelect: (id: string) => void;
  onNew: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}

/**
 * Every conversation in one list: the Assistant pinned on top, then tenant,
 * agent / service-desk, SMS and WhatsApp threads with a preview, time and
 * unread badge. Same list, rows and states as the tenant app (@reloam/ui).
 */
export default function ConversationSwitcher({ rows, onSelect, onNew, onRefresh, refreshing }: ConversationSwitcherProps) {
  return (
    <ConversationList
      loading={refreshing}
      onRefresh={onRefresh}
      actions={
        <button
          type="button"
          onClick={onNew}
          aria-label="New conversation"
          title="New conversation"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <HiPlus className="h-5 w-5" aria-hidden />
        </button>
      }
    >
      {rows.map((row) => (
        <ConversationRow
          key={row.id}
          avatar={<ChannelAvatar kind={row.kind} />}
          name={row.name}
          preview={row.preview}
          timeLabel={formatConversationTime(row.time)}
          unreadCount={row.unreadCount}
          isActive={row.isActive}
          ariaLabel={`${row.name}, ${CHANNEL_LABEL[row.kind]}${row.unreadCount ? `, ${row.unreadCount} unread` : ''}`}
          onSelect={() => onSelect(row.id)}
        />
      ))}
    </ConversationList>
  );
}
